import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Stack,
  Alert,
  Divider,
  Button,
  Tooltip,
} from "@mui/material";
import ReplayIcon from "@mui/icons-material/Replay";
import api from "../../utils/api";

/* =========================================================
   HELPERS
   ========================================================= */

// Trend meta
function getTrendMeta(trend, currentRank, previousRank) {
  const t = (trend || "").toUpperCase();

  if (t === "UP") {
    return {
      icon: "⬆️",
      label: previousRank
        ? `Improved from #${previousRank} → #${currentRank}`
        : "Improved ranking",
      color: "success",
    };
  }
  if (t === "DOWN") {
    return {
      icon: "⬇️",
      label: previousRank
        ? `Dropped from #${previousRank} → #${currentRank}`
        : "Dropped ranking",
      color: "error",
    };
  }
  if (t === "SAME") {
    return {
      icon: "➡️",
      label: `No change (#${currentRank})`,
      color: "info",
    };
  }
  if (t === "NEW") {
    return {
      icon: "🆕",
      label: "New this week",
      color: "warning",
    };
  }

  return {
    icon: "➡️",
    label: "No data",
    color: "default",
  };
}

function fmt2(n) {
  const x = Number(n);
  return Number.isFinite(x) ? x.toFixed(2) : "—";
}

/* =========================================================
   SHARED TABLE (ADMIN VIEW)
   ========================================================= */
function LeaderboardTable({ rows }) {
  if (!rows || rows.length === 0) {
    return <Alert severity="info">No submissions for this week.</Alert>;
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell width={80}>Rank</TableCell>
          <TableCell>Student</TableCell>

          {/* NEW */}
          <TableCell width={90}>Trend</TableCell>

          <TableCell width={180}>Subject</TableCell>
          <TableCell width={120}>Best</TableCell>
          <TableCell width={120}>Avg</TableCell>

          {/* NEW */}
          <TableCell width={140}>Weighted</TableCell>

          <TableCell width={90}>Exams</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {rows.map((row) => {
          const rank = row.current_rank ?? row.rank;
          const trendMeta = getTrendMeta(
            row.trend,
            rank,
            row.previous_rank
          );

          return (
            <TableRow key={`${row.student_id}-${rank}`} hover>
              <TableCell>
                <Typography fontWeight={900}>#{rank}</Typography>
              </TableCell>

              <TableCell>
                <Typography fontWeight={700}>{row.full_name}</Typography>
              </TableCell>

              {/* TREND */}
              <TableCell>
                <Tooltip title={trendMeta.label}>
                  <Chip
                    label={trendMeta.icon}
                    size="small"
                    color={trendMeta.color}
                    variant="outlined"
                    sx={{ fontWeight: 900 }}
                  />
                </Tooltip>
              </TableCell>

              {/* SUBJECT */}
              <TableCell>
                {row.subject_name ? (
                  <Chip
                    label={`${row.subject_name}${
                      row.subject_code ? ` (${row.subject_code})` : ""
                    }`}
                    size="small"
                    variant="outlined"
                    sx={{ fontWeight: 900 }}
                  />
                ) : (
                  "—"
                )}
              </TableCell>

              {/* BEST */}
              <TableCell>
                <Chip
                  label={`${row.best_score ?? "—"}%`}
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 900 }}
                />
              </TableCell>

              {/* AVG */}
              <TableCell>
                <Chip
                  label={`${row.avg_score ?? "—"}%`}
                  color="info"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 900 }}
                />
              </TableCell>

              {/* WEIGHTED */}
              <TableCell>
                <Chip
                  label={fmt2(row.weighted_score)}
                  color="primary"
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 900 }}
                />
              </TableCell>

              {/* EXAMS */}
              <TableCell>
                <Chip
                  label={row.exams_taken ?? 0}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 900 }}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/* =========================================================
   ADMIN WEEKLY LEADERBOARD PAGE
   ========================================================= */
export default function AdminWeeklyLeaderboard() {
  const navigate = useNavigate();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  const [subjectAll, setSubjectAll] = useState([]);
  const [mathOnly, setMathOnly] = useState([]);
  const [postUtme, setPostUtme] = useState([]);

  /* -----------------------------
     Load all three leaderboards
     ----------------------------- */
  const loadAllLeaderboards = async () => {
    setLoading(true);
    try {
      const [subAll, math, utme] = await Promise.all([
        api.get("/leaderboard/weekly?exam_type=SUBJECT_PRACTICE"),
        api.get("/leaderboard/weekly?exam_type=SUBJECT_PRACTICE&subject_id=2"),
        api.get("/leaderboard/weekly?exam_type=POST_UTME"),
      ]);

      setSubjectAll(subAll.data?.leaderboard || []);
      setMathOnly(math.data?.leaderboard || []);
      setPostUtme(utme.data?.leaderboard || []);
    } catch (err) {
      console.error("Admin weekly leaderboard load error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllLeaderboards();
  }, []);

  return (
    <Box>
      {/* ================= HEADER ================= */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={900}>
            📊 Weekly Leaderboard Monitor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Admin overview of weekly competitions (ranked by weighted score)
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={loadAllLeaderboards}
            sx={{ fontWeight: 800 }}
          >
            Refresh
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/admin/leaderboard-manager")}
            sx={{ fontWeight: 800 }}
          >
            Manage Leaderboards
          </Button>
		  
		  <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/admin/weekly-announcement")}
            sx={{ fontWeight: 800 }}
          >
           Announcement
          </Button>
		  
		  
		    <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/admin/jamb-learderboard")}
            sx={{ fontWeight: 800 }}
          >
        JAMB 
          </Button>
		  

		  
        </Stack>
      </Stack>

      {/* ================= TABS ================= */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="📘 Subject Practice (All)" />
          <Tab label="📐 Mathematics Only" />
          <Tab label="🎯 Post-UTME" />
        </Tabs>
      </Paper>

      {/* ================= CONTENT ================= */}
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box textAlign="center" py={5}>
            <CircularProgress />
            <Typography mt={2}>Loading weekly leaderboards…</Typography>
          </Box>
        ) : (
          <>
            {tab === 0 && <LeaderboardTable rows={subjectAll} />}
            {tab === 1 && <LeaderboardTable rows={mathOnly} />}
            {tab === 2 && <LeaderboardTable rows={postUtme} />}
          </>
        )}
      </Paper>

      <Divider sx={{ my: 2 }} />

      {/* ================= FOOTER INFO ================= */}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Chip label="🔁 Auto-resets weekly (Monday)" />
        <Chip label="⚖ Ranking: Weighted Score (Best × Avg)" color="info" />
        <Chip label="👁 Read-only (Admin)" color="warning" />
      </Stack>
    </Box>
  );
}
